// Gateway との通信。Firebase IDトークンを付与して呼び出す純粋な非同期関数群。
// 例外メッセージは必ず日本語にする（英語の "Failed to fetch" 等をUIに出さない）。
import { auth } from '@/firebase/config';
import { GATEWAY_URL } from '../config';
import type { LlmMessage } from '../types';
import type { ToolCall, ToolDefinition } from '../tools/types';

/** LLM が返すアシスタントの応答。tool_calls があればツール実行が要求されている。 */
export interface AssistantReply {
  content: string;
  toolCalls: ToolCall[];
}

/** ログイン中ユーザーのIDトークンを取得。未ログイン・取得失敗なら null。 */
async function getIdToken(): Promise<string | null> {
  try {
    const current = auth.currentUser;
    return current ? await current.getIdToken() : null;
  } catch {
    return null; // 取れなくても送信は試みる（Gatewayが401で正しく弾く）
  }
}

/** Gateway の死活状態と、そこで稼働しているモデル。 */
export interface HealthStatus {
  /** Ollama まで到達できるか（睡眠判定に使用） */
  up: boolean;
  /** 稼働中のモデル名（例 'gemma4:e4b'）。取得できなければ null */
  model: string | null;
}

/**
 * Gateway の死活とモデルを確認する。失敗は例外にせず up:false を返す（業務を妨げない）。
 *
 * ※ モデル名は Gateway しか知らない。LLM 自身は「自分がどのタグ(e2b/e4b)で配信されているか」を
 *   知らず、聞いても「私は Gemma 4 です」としか答えられない（重みに入っていない情報のため）。
 *   どちらが動いているかを人が確認できるよう、ここで拾って UI に出す。
 */
export async function checkHealth(): Promise<HealthStatus> {
  try {
    const res = await fetch(`${GATEWAY_URL}/healthz`);
    if (!res.ok) return { up: false, model: null };
    const body = (await res.json()) as { ollamaUp?: boolean; primary?: string };
    return { up: body.ollamaUp === true, model: body.primary ?? null };
  } catch {
    return { up: false, model: null };
  }
}

/**
 * チャット送信。tools を渡すと LLM が tool_calls を返すことがある。
 * ツールの「実行」はブラウザ側で行うため、この関数は結果を中継するだけ。
 */
export async function sendChat(
  messages: LlmMessage[],
  options?: {
    tools?: ToolDefinition[];
    model?: string;
    /**
     * 思考(thinking)を出力させるか。
     * gemma4 は思考を書き出すモデルで、CPU推論では生成時間の大半を占める。
     * ツール判断には思考が必要（切ると呼べなくなる）が、
     * ツール結果を要約するだけの最終回答では不要。省略時はモデル既定（思考あり）。
     */
    think?: boolean;
  }
): Promise<AssistantReply> {
  const token = await getIdToken();

  let res: Response;
  try {
    res = await fetch(`${GATEWAY_URL}/chat`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        messages,
        model: options?.model,
        tools: options?.tools,
        think: options?.think,
        stream: false,
      }),
    });
  } catch {
    throw new Error('AI秘書に接続できませんでした。時間をおいて再度お試しください。');
  }

  if (!res.ok) {
    // Gateway は日本語のエラー文言を返す（例: 認証に失敗しました）。無ければ汎用文言。
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `AI秘書がエラーを返しました（${res.status}）`);
  }

  const data = (await res.json().catch(() => ({}))) as {
    message?: { content?: string; tool_calls?: ToolCall[] };
  };
  return {
    content: data.message?.content ?? '',
    toolCalls: data.message?.tool_calls ?? [],
  };
}
